import React, { createRef, useEffect, useState } from 'react';

import {
  InputDefaultValidator,
  InputValidator
} from '../../../utils/validators/form-validators';
import './input.scss';

interface Props {
  floatingLabel?: boolean;
  focus?: boolean;
  errorMessage?: string;
  hideLabel?: boolean;
  hintMessage?: string;
  label?: string;
  name: string;
  placeholder?: string;
  showValidation?: boolean;
  type?: 'email' | 'number' | 'password' | 'text';
  validator?: InputValidator;
  value: string;
  onChange?: (newValue: string) => any;
  onValidate?: (isValid: boolean) => any;
}

const Input = ({
  floatingLabel,
  focus,
  errorMessage: customErrorMessage,
  hideLabel,
  hintMessage,
  label,
  name,
  placeholder = '',
  showValidation,
  type = 'text',
  validator: validate = InputDefaultValidator,
  value,
  onChange,
  onValidate
}: Props) => {
  let hasBeenValidatedOnce = false;
  const placeholderText = floatingLabel ? '' : placeholder;
  const [hasHint, setHasHint] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [floatLabel, setFloatLabel] = useState(false);

  const ref = createRef<HTMLInputElement>();

  useEffect(() => {
    if (focus && ref.current) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runValidator = (newValue: string) => {
    if (validate) {
      const { passed, message } = validate(customErrorMessage)(newValue);
      setHasError(!passed);
      setErrorMessage(message);
      if (!showValidation) {
        setHasHint(!passed);
      }
      if (onValidate) {
        onValidate(passed);
      }
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    if (showValidation && !hasBeenValidatedOnce) {
      runValidator(value);
      hasBeenValidatedOnce = true;
    }
  }, [showValidation]);

  const updateValue = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const inputElement = evt.nativeEvent.srcElement as HTMLInputElement;
    const newValue = inputElement.value;
    if (newValue.length) {
      setFloatLabel(true);
    } else {
      setFloatLabel(false);
    }

    runValidator(newValue);
  };

  const classes = `input__field ${
    hasError && showValidation ? 'input__field--error' : ''
  } ${floatLabel ? 'input__field--float' : ''}`;

  return (
    <div
      className={`input ${floatingLabel ? 'input--has-floating-label' : ''}`}
    >
      {hasError && showValidation && (
        <span className="input__error">{errorMessage}</span>
      )}
      {hasHint && hintMessage && (
        <span className="input__hint">{hintMessage}</span>
      )}
      <div className="input__wrapper">
        {label && !floatingLabel && !hideLabel && (
          <label className="input__label" htmlFor={name}>
            {label}
          </label>
        )}
        {!floatingLabel && hideLabel && (
          <label
            className="input__label input__label--screen-reader"
            htmlFor={name}
          >
            {label || placeholder}
          </label>
        )}
        <input
          className={classes}
          id={name}
          name={name}
          type={type}
          value={value}
          placeholder={placeholderText}
          onChange={updateValue}
          ref={ref}
        />
        {floatingLabel && label && !hideLabel && (
          <label
            className={`input__label input__label--floating ${
              floatLabel ? 'input__label--float' : ''
            }`}
            htmlFor={name}
          >
            {label}
          </label>
        )}
      </div>
    </div>
  );
};

export default Input;
